import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ReactComponent as CloseIcon } from '@blink/assets/src/icons/close.svg';

import { Store } from '@/shared/store';
import { showModalAction } from '@/shared/store/modals/actions';
import { ModalName } from '@/shared/store/modals/constants';
import { web3wallet } from '@/shared/hooks/useWalletConnect';
import {
    Tooltip,
    TooltipTrigger,
    TooltipContent,
    TooltipProvider,
    Button,
    Text,
} from '@/shared/ui';

export type WalletConnectStatusProps = {
    disconnect?: () => void;
};

const TEXTS = {
    marginAccountDisconnectionTitle: 'Margin Account Disconnection',
    tooltipConnected: (connectedDapp: string) =>
        `Wallet connected to ${connectedDapp} with this wallet`,
    tooltipDisconnect: (connectedDapp: string) => `Disconnect ${connectedDapp}`,
    connectedTo: 'Connected to',
    hoverOpacityClass: 'hover:opacity-80',
};

export const WalletConnectStatus = ({ disconnect }: WalletConnectStatusProps) => {
    const { address, connectedDapp, url } = useSelector((state: Store) => ({
        address: state.wallets.active.address,
        connectedDapp: state.wallets.walletConnect?.connectedDapp?.name,
        url: state.wallets.walletConnect?.connectedDapp?.url,
    }));

    const dispatch = useDispatch();

    if (!web3wallet || !address || !connectedDapp) {
        return null;
    }

    const maDisconnectModalTitle = () => (
        <div className='flex items-center justify-center'>
            <div className='text-xl text-foreground'>{TEXTS.marginAccountDisconnectionTitle}</div>
        </div>
    );

    const showMADisconnectionModal = () => {
        dispatch(
            showModalAction({
                modalName: ModalName.MA_DISCONNECTION,
                modalTitle: maDisconnectModalTitle() as any,
                modalProps: {
                    disconnectAction: disconnect,
                },
            }),
        );
    };

    return (
        <span className='flex items-center'>
            <TooltipProvider>
                <Tooltip>
                    <TooltipTrigger asChild>
                        <span className='underline text-content-foreground'>
                            {TEXTS.connectedTo}{' '}
                            <Text
                                type={Text.types.Link}
                                href={url}
                                target='_blank'
                                rel='noreferrer'
                                className='hover:opacity-80'
                            >
                                {connectedDapp}
                            </Text>
                        </span>
                    </TooltipTrigger>
                    <TooltipContent side='bottom' sideOffset={30}>
                        <Text type={Text.types.Paragraph}>
                            {TEXTS.tooltipConnected(connectedDapp)}
                        </Text>
                    </TooltipContent>
                </Tooltip>
                <Tooltip>
                    <TooltipTrigger asChild>
                        <span>
                            <Button
                                variant={Button.types.Ghost}
                                size={Button.sizes.Small}
                                onClick={showMADisconnectionModal}
                                className='ml-2 p-0 w-6 h-6'
                            >
                                <CloseIcon className='w-4 h-4 text-content-foreground' />
                            </Button>
                        </span>
                    </TooltipTrigger>
                    <TooltipContent side='bottom' sideOffset={30}>
                        <Text type={Text.types.Paragraph}>
                            {TEXTS.tooltipDisconnect(connectedDapp)}
                        </Text>
                    </TooltipContent>
                </Tooltip>
            </TooltipProvider>
        </span>
    );
};
